import { supabase } from '../lib/supabaseClient';
import { AppUser } from '../contexts/SupabaseAuthContext';

class SupabaseUserService {
  private listeners: Array<() => void> = [];

  // Subscribe to user data changes
  subscribe(listener: () => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Notify all listeners when user data changes
  private notifyUserDataChange() {
    this.listeners.forEach(listener => listener());
  }

  // Get user TIL balance
  async getUserTILBalance(userId: string): Promise<number> {
    const { data, error } = await supabase
      .from('users')
      .select('til_balance')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching TIL balance:', error);
      return 0;
    }

    return data?.til_balance || 0;
  }

  // Update user TIL balance
  async updateUserTILBalance(userId: string, newBalance: number): Promise<void> {
    const { error } = await supabase
      .from('users')
      .update({ til_balance: newBalance })
      .eq('id', userId);

    if (error) {
      console.error('Error updating TIL balance:', error);
      throw error;
    }

    this.notifyUserDataChange();
  }

  // Add TIL balance (for approved Extra Hours)
  // Uses SECURITY DEFINER function to bypass RLS
  async addTILBalance(userId: string, minutes: number, requestId?: string): Promise<void> {
    const { data, error } = await supabase.rpc('update_user_til_balance', {
      p_user_id: userId,
      p_minutes_delta: minutes,
      p_request_id: requestId || null,
      p_type: 'earn',
      p_description: `Earned ${minutes} minutes from approved extra hours`
    });

    if (error) {
      console.error('Error adding TIL balance:', error);
      throw error;
    }

    const result = data?.[0];
    if (!result?.success) {
      throw new Error(result?.error_message || 'Failed to update TIL balance');
    }

    console.log(`Added ${minutes} minutes TIL balance. New balance: ${result.new_balance}`);
    this.notifyUserDataChange();
  }

  // Deduct TIL balance (for approved TIL requests)
  // Uses SECURITY DEFINER function to bypass RLS
  async deductTILBalance(userId: string, minutes: number, requestId?: string): Promise<void> {
    const { data, error } = await supabase.rpc('update_user_til_balance', {
      p_user_id: userId,
      p_minutes_delta: minutes,
      p_request_id: requestId || null,
      p_type: 'spend',
      p_description: `Spent ${minutes} minutes for approved TIL request`
    });

    if (error) {
      console.error('Error deducting TIL balance:', error);
      throw error;
    }

    const result = data?.[0];
    if (!result?.success) {
      throw new Error(result?.error_message || 'Failed to update TIL balance');
    }

    console.log(`Deducted ${minutes} minutes TIL balance. New balance: ${result.new_balance}`);
    this.notifyUserDataChange();
  }

  // Adjust TIL balance (for cancelling approved requests)
  // Uses SECURITY DEFINER function to bypass RLS
  // minutesDelta can be positive (add back) or negative (remove)
  async adjustTILBalance(
    userId: string,
    minutesDelta: number,
    requestId: string,
    description: string
  ): Promise<{ success: boolean; newBalance?: number; error?: string }> {
    const { data, error } = await supabase.rpc('update_user_til_balance', {
      p_user_id: userId,
      p_minutes_delta: minutesDelta, // Pass the signed value
      p_request_id: requestId,
      p_type: 'adjustment',
      p_description: description
    });

    if (error) {
      console.error('Error adjusting TIL balance:', error);
      return { success: false, error: error.message };
    }

    const result = data?.[0];
    if (!result?.success) {
      return { success: false, error: result?.error_message || 'Failed to adjust TIL balance' };
    }

    console.log(`Adjusted TIL balance by ${minutesDelta} minutes. New balance: ${result.new_balance}`);
    this.notifyUserDataChange();
    return { success: true, newBalance: result.new_balance };
  }

  // Get all users (for admin/management pages)
  async getAllUsers(): Promise<AppUser[]> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('full_name');

    if (error) {
      console.error('Error fetching users:', error);
      return [];
    }

    return data as AppUser[];
  }

  // Get all users including pending users (for admin views)
  async getAllUsersIncludingPending(): Promise<any[]> {
    try {
      // Get regular users
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('*')
        .order('full_name');

      if (usersError) {
        console.error('Error fetching users:', usersError);
        return [];
      }

      // Get pending users
      const { data: pendingUsers, error: pendingError } = await supabase
        .from('pending_users')
        .select('*')
        .order('full_name');

      if (pendingError) {
        console.error('Error fetching pending users:', pendingError);
        return users || [];
      }

      // Combine both lists, marking pending users
      const allUsers = [
        ...(users || []).map((user: any) => ({ ...user, isPending: false })),
        ...(pendingUsers || []).map((user: any) => ({ ...user, isPending: true }))
      ];

      return allUsers;
    } catch (err) {
      console.error('Unexpected error fetching all users:', err);
      return [];
    }
  }

  // Get user by ID
  async getUserById(userId: string): Promise<AppUser | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching user:', error);
      return null;
    }

    return data as AppUser;
  }

  // Get users by department
  async getUsersByDepartment(departmentId: string): Promise<AppUser[]> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('department_id', departmentId)
      .order('full_name');

    if (error) {
      console.error('Error fetching users by department:', error);
      return [];
    }

    return data as AppUser[];
  }

  // Update user
  async updateUser(userId: string, updates: Partial<AppUser>): Promise<boolean> {
    console.log('[updateUser] Attempting to update user:', userId, updates);

    const { data, error, count } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select();

    console.log('[updateUser] Supabase response:', { data, error, count, rowsAffected: data?.length });

    if (error) {
      console.error('[updateUser] Error updating user:', error);
      return false;
    }

    if (!data || data.length === 0) {
      console.error('[updateUser] No rows were updated! This likely means RLS blocked the update.');
      console.error('[updateUser] User ID:', userId);
      console.error('[updateUser] Updates:', updates);
      return false;
    }

    console.log('[updateUser] Successfully updated user:', data);
    this.notifyUserDataChange();
    return true;
  }

  // Update user roles
  async updateUserRoles(userId: string, roles: string[]): Promise<boolean> {
    const { error } = await supabase
      .from('users')
      .update({ roles })
      .eq('id', userId);

    if (error) {
      console.error('Error updating user roles:', error);
      return false;
    }

    this.notifyUserDataChange();
    return true;
  }

  // Create user (admin only)
  async createUser(userData: Partial<AppUser>): Promise<AppUser | null> {
    const { data, error } = await supabase
      .from('users')
      .insert([userData])
      .select()
      .single();

    if (error) {
      console.error('Error creating user:', error);
      return null;
    }

    this.notifyUserDataChange();
    return data as AppUser;
  }

  // Create pending user (for users who haven't logged in yet)
  async createPendingUser(userData: any): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('pending_users')
        .insert([userData])
        .select()
        .single();

      if (error) {
        console.error('Error creating pending user:', error);
        return { success: false, error: error.message };
      }

      this.notifyUserDataChange();
      return { success: true };
    } catch (err) {
      console.error('Unexpected error creating pending user:', err);
      return { success: false, error: 'An unexpected error occurred' };
    }
  }
}

// Create and export singleton instance
export const supabaseUserService = new SupabaseUserService();
